import type { LucideIcon } from 'lucide-react'
import { FileText } from 'lucide-react'
import { cn } from '@/lib/utils'
import { opensInReader } from '@/lib/examPdf'
import { openPdfReader, type PdfReaderDoc } from '@/hooks/usePdfReader'

/**
 * **The** PDF button. One published document, opened in the app's reader.
 *
 * Wherever the app offers a PDF — a sitting's examiner's report and its
 * solutions on the past-paper shelf, an exam's published syllabus in the
 * study guide's header, a resource page's "Read PDF", the paper behind the
 * question on screen, a source the Fact Check panel was checked against — it is
 * this button, so a candidate never loses their place to a browser tab.
 *
 * It stays an anchor to the publisher underneath. A plain left click reads the
 * document here; ⌘/ctrl-click, middle-click, shift-click and long-press keep
 * doing what a link does, the real URL stays visible in the status bar, and a
 * source the proxy won't serve (`lib/examPdf.ts`) is left as an ordinary
 * out-link rather than opening a panel that can't load. `opensInReader` is that
 * rule, in one tested place.
 *
 * Where the document is read is the host's to say. By default it is the app's
 * reader (`hooks/usePdfReader.ts`); a page of the concept popup passes `onRead`
 * instead, and its document opens as the next page of the popup's stack.
 */

interface PdfLinkButtonProps {
  /** The publisher's URL for the document. */
  url: string
  /** What the button says — "Examiner's Report", "Syllabus", "Read PDF". */
  label: string
  /** The reader's own title; defaults to `label`. */
  title?: string
  /** The reader's subtitle — which paper or work this belongs to. */
  subtitle?: string
  /** The page the citation points at (1-based, as the PDF numbers it); the reader opens there. */
  page?: number
  /**
   * Read the document here rather than in the app's reader. The concept popup
   * passes this for the pages of its stack, so a PDF opened from one of them
   * stacks on top of the page like a followed link instead of covering the
   * pane. The click rules are the same either way — a modified click is still a
   * link, and a source the proxy won't serve is still an out-link.
   */
  onRead?: (doc: PdfReaderDoc) => void
  /** Force the out-link, for a surface the reader must not cover. */
  linkOnly?: boolean
  /**
   * Runs when the reader takes the click. A host whose whole job was to point
   * at this document (the quiz's Question info panel) uses it to get out of the
   * way; a host the reader is read *beside* leaves it alone.
   */
  onOpen?: () => void
  /** The leading icon. A page of text unless the host has a better one. */
  icon?: LucideIcon
  /** The `PDF` chip after the label. On unless the label already says it. */
  chip?: boolean
  /** The `title` attribute — a tooltip, not the reader's heading. */
  tooltip?: string
  /**
   * What a screen reader hears, when the button's own words don't name the
   * document ("Read PDF" on a resource card says nothing about *which*).
   */
  ariaLabel?: string
  className?: string
  /**
   * Classes for the label alone — a host with little room (the study guide's
   * sticky header on a phone) can hide the words and keep the icon, since the
   * aria-label and tooltip still name the document.
   */
  labelClassName?: string
}

/** An unmodified left click — what the aria-label is written for. */
const PLAIN_CLICK = {
  metaKey: false,
  ctrlKey: false,
  shiftKey: false,
  altKey: false,
  button: 0,
} as const

/** The shape every PDF button shares: a thumb-sized filled target, not fine print. */
const BASE_CLASS =
  'inline-flex min-h-[36px] items-center gap-2 rounded-md border border-border px-3 py-1.5 ' +
  'text-sm font-medium text-foreground no-underline shadow-sm transition-colors ' +
  'hover:bg-accent hover:text-accent-foreground ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'

export function PdfLinkButton({
  url,
  label,
  title,
  subtitle,
  page,
  onRead,
  linkOnly = false,
  onOpen,
  icon: Icon = FileText,
  chip = true,
  tooltip,
  ariaLabel,
  className,
  labelClassName,
}: PdfLinkButtonProps) {
  // Whether *a* plain click would open here, for the label a screen reader
  // hears. The click itself re-decides with the modifiers it actually carries.
  const readsHere = opensInReader(url, PLAIN_CLICK, linkOnly)
  return (
    <a
      href={page ? `${url}#page=${page}` : url}
      target="_blank"
      rel="noreferrer"
      title={tooltip}
      aria-label={ariaLabel ?? (readsHere ? `View ${label} (PDF)` : `Open ${label} (PDF)`)}
      onClick={e => {
        if (!opensInReader(url, e, linkOnly)) return
        e.preventDefault()
        onOpen?.()
        const doc = { url, title: title ?? label, subtitle, page }
        if (onRead) onRead(doc)
        else openPdfReader(doc)
      }}
      className={cn('not-prose bg-card', BASE_CLASS, className)}
    >
      <Icon className="h-4 w-4 shrink-0 text-primary" aria-hidden />
      {labelClassName ? <span className={labelClassName}>{label}</span> : label}
      {chip && (
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          PDF
        </span>
      )}
    </a>
  )
}

/**
 * A PDF the prose links to — a markdown `[text](….pdf)` in a study guide, a
 * concept page or a question's explanation. It stays the link the author wrote
 * (same underline, same words), but a plain click reads the document in the
 * app the way `PdfLinkButton` does, under the same `opensInReader` rule; any
 * other link is an ordinary out-link in a new tab.
 *
 * The reader is titled with the link's own words. A list row that links a
 * document by a bare word ("questions and solutions") names the document in
 * its lead, so that lead — the row's text before its first colon or dash —
 * becomes the subtitle.
 */
export function ProseLink({
  href,
  children,
  onRead,
  ...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & { onRead?: (doc: PdfReaderDoc) => void }) {
  if (!href) return <a {...rest}>{children}</a>
  const url = href
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      {...rest}
      onClick={e => {
        if (!opensInReader(url, e)) return
        e.preventDefault()
        const doc = { url, ...proseLinkTitles(e.currentTarget) }
        if (onRead) onRead(doc)
        else openPdfReader(doc)
      }}
    >
      {children}
    </a>
  )
}

function proseLinkTitles(anchor: HTMLAnchorElement): { title: string; subtitle?: string } {
  const words = (anchor.textContent ?? '').trim()
  const title = words ? words[0].toUpperCase() + words.slice(1) : 'PDF'
  const row = anchor.closest('li')
  const lead = row?.textContent?.split(/:| — | – /)[0].replace(/\s+/g, ' ').trim()
  const subtitle = lead && lead.toLowerCase() !== title.toLowerCase() && lead.length <= 100 ? lead : undefined
  return { title, subtitle }
}
